import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import Button from '../UI/Button';
import { dietService } from '../../services/dietService';
import { Menu, CreateMenuRequest, UpdateMenuRequest } from '../../types/diet';

interface Props {
  menu?: Menu | null;
  onClose: () => void;
  onSuccess: () => void;
}

const MenuModal: React.FC<Props> = ({ menu, onClose, onSuccess }) => {
  const [error, setError] = useState<string | null>(null);
  const isEditing = !!menu;
  const navigate = useNavigate();

  const { register, handleSubmit, formState: { errors }, reset } = useForm<CreateMenuRequest>({
    defaultValues: { name: menu?.name || '', description: menu?.description || '', status: menu?.status || 'ACTIVE' }
  });

  useEffect(() => { if (menu) reset({ name: menu.name, description: menu.description || '', status: menu.status }); }, [menu, reset]);

  const createMutation = useMutation({
    mutationFn: (data: CreateMenuRequest) => dietService.createMenu(data),
    onSuccess: (res) => {
      try {
        const created = (res as any)?.menu;
        if (created?.id) {
          onClose();
          navigate(`/setup/menus/${created.id}/manage`);
          return;
        }
      } catch {}
      onSuccess();
    },
    onError: (e: any) => setError(e?.data?.message || e?.response?.data?.message || e?.message || 'Failed to create menu'),
  });

  const updateMutation = useMutation({
    mutationFn: (data: UpdateMenuRequest) => dietService.updateMenu(menu!.id, data),
    onSuccess,
    onError: (e: any) => setError(e?.data?.message || e?.response?.data?.message || e?.message || 'Failed to update menu'),
  });

  const onSubmit = (data: CreateMenuRequest) => { setError(null); isEditing ? updateMutation.mutate(data) : createMutation.mutate(data); };
  const isLoading = createMutation.isPending || updateMutation.isPending;


  return (
    <Transition appear show={true} as={React.Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child as={React.Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child as={React.Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-lg bg-white p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex items-center justify-between mb-4">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">{isEditing ? 'Edit Menu' : 'Create Menu'}</Dialog.Title>
                  <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><XMarkIcon className="h-6 w-6" /></button>
                </div>
                {error && (<div className="mb-4 rounded-lg bg-red-50 p-4"><div className="text-sm text-red-700">{error}</div></div>)}
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                  <div>
                    <label className="form-label">Name *</label>
                    <input {...register('name', { required: 'Name is required', maxLength: { value: 100, message: 'Max 100 chars' } })} type="text" className="form-input" placeholder="Enter menu name" />
                    {errors.name && <p className="form-error">{errors.name.message}</p>}
                  </div>
                  <div>
                    <label className="form-label">Description</label>
                    <textarea {...register('description')} className="form-input" rows={3} placeholder="Optional description" />
                  </div>
                  <div>
                    <label className="form-label">Status</label>
                    <select {...register('status')} className="form-input">
                      <option value="ACTIVE">Active</option>
                      <option value="INACTIVE">Inactive</option>
                    </select>
                  </div>
                  <div className="flex justify-between items-center pt-4">
                    {isEditing ? (
                      <Button type="button" variant="outline" onClick={() => { onClose(); navigate(`/setup/menus/${menu!.id}/manage`); }}>Manage</Button>
                    ) : <span />}
                    <div className="flex space-x-3">
                      <Button type="button" variant="secondary" onClick={onClose} disabled={isLoading}>Cancel</Button>
                      <Button type="submit" loading={isLoading}>{isEditing ? 'Update' : 'Create'}</Button>
                    </div>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default MenuModal;

